import { BaseDisplayer } from './BaseDisplayer';
import { TEDParseDateTimeMode, ValueType } from '../FormatterTypes';
import { nextFormatItem } from '../Utils/Other';
import {
  formatterBoolParamDef,
  formatterStrParam,
  formatterStrParamDef,
  formatterSetBoolParam,
} from '../Utils/ParamUtils';
import {
  getMonthText,
  encodeDate,
  getWeekNr,
  getQuarter,
  getQuarterText,
  extractDate,
} from '../Utils/TimeDateUtils';
import { hasValue, intToFixString } from '../Utils/ValueUtils';

export interface IInternalDateFormat {
  ty: number;
  tm: number;
  td: number;
  th: number;
  tn: number;
  ts: number;
  tms: number;
}

abstract class TEDCustomDateTimeDisplayer extends BaseDisplayer {
  abstract mode(): TEDParseDateTimeMode;

  defaultParams(): string {
    let result = '';
    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'DateTimeSep=" ";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmYMD,
        TEDParseDateTimeMode.pdmYM,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateString,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'DateSep="-";';
    }

    if (this.mode() === TEDParseDateTimeMode.pdmYW) {
      result += 'DateSep="v";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmYMD,
        TEDParseDateTimeMode.pdmYM,
        TEDParseDateTimeMode.pdmYW,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateString,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'HideCentury="N";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmHMS,
        TEDParseDateTimeMode.pdmHM,
        TEDParseDateTimeMode.pdmH,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'TimeSep=":";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'HideZeroTime="N"';
    }

    switch (this.mode()) {
      case TEDParseDateTimeMode.pdmDateString:
      case TEDParseDateTimeMode.pdmDateTimeString:
        result += 'Format="";MonthTexts="";DayTexts="";';
        break;
      case TEDParseDateTimeMode.pdmGeneral:
        result +=
          'Format="yyyy-mm-dd";MonthTexts="";DayTexts="";AmStr="fm";PmStr="em";';
        break;
    }

    return result.replace(/^;+|;+$/g, ''); // trim leading and ending ';'
  }

  makeFormatString(params: string, displayText: boolean): string | null {
    let formatString = '';

    const hideCentury: boolean = formatterBoolParamDef(
      'HideCentury',
      params,
      this.defaultParams()
    );

    const hideTime = formatterBoolParamDef(
      'HideTime',
      params,
      this.defaultParams()
    );
    const format = formatterStrParam('Format', params, this.defaultParams());

    switch (this.mode()) {
      case TEDParseDateTimeMode.pdmYMD:
        formatString = yearPattern(hideCentury) + '-mm-dd';
        break;
      case TEDParseDateTimeMode.pdmYM:
        formatString = yearPattern(hideCentury) + '-mm';
        break;
      case TEDParseDateTimeMode.pdmY:
        formatString = yearPattern(hideCentury);
        break;
      case TEDParseDateTimeMode.pdmHMS:
        formatString = 'hh:nn:ss';
        break;
      case TEDParseDateTimeMode.pdmHM:
        formatString = 'hh:nn';
        break;
      case TEDParseDateTimeMode.pdmH:
        formatString = 'hh';
        break;
      case TEDParseDateTimeMode.pdmYMDHM:
        if (hideTime) {
          formatString = yearPattern(hideCentury) + '--mm-dd';
        } else {
          formatString = yearPattern(hideCentury) + '-mm-dd.hh:nn';
        }
        break;
      case TEDParseDateTimeMode.pdmYW:
        formatString = weekYearPattern(hideCentury) + ' -w';
        break;
      case TEDParseDateTimeMode.pdmDateTimeString:
        if (!format.result) {
          formatString = yearPattern(hideCentury) + '-mm-dd.hh:nn';
        } else {
          formatString = format.value;
        }
        break;
      case TEDParseDateTimeMode.pdmDateString: {
        if (!format.result) {
          formatString = yearPattern(hideCentury) + '-mm-dd';
        } else {
          formatString = format.value;
        }
        break;
      }
      default:
        // pdmGeneral
        formatString = formatterStrParamDef(
          'Format',
          params,
          this.defaultParams(),
          ''
        );
    }

    return formatString === '' ? null : formatString;
  }

  defaultDisplayLength(params: string = ''): number {
    throw new Error('Not implemented yet');
  }

  preDisplay(
    value: ValueType,
    displayText: boolean = true,
    params: string = ''
  ): string {
    throw new Error('Not implementd yet');
  }

  display(
    value: ValueType,
    displayText: boolean = true,
    params: string = ''
  ): string {
    let result = '';
    let tmpDate: Date | null;
    if (hasValue(value)) {
      const date = this.asDateTime(value, params, this.defaultParams());
      let dsepcnt = (date.tm >= 0 ? 1 : 0) + (date.td >= 0 ? 1 : 0);
      let tsepcnt = (date.tn >= 0 ? 1 : 0) + (date.ts >= 0 ? 1 : 0);
      let tmpparams = params;

      if (
        date.th <= 0 &&
        date.tn <= 0 &&
        date.ts <= 0 &&
        date.tms <= 0 &&
        formatterBoolParamDef('HideZeroTime', params, this.defaultParams())
      ) {
        tmpparams = formatterSetBoolParam('HideTime', 'Y', tmpparams);
      }
      let formatString = this.makeFormatString(tmpparams, displayText);
      if (formatString === null) {
        return '';
      } else {
        let startIndex = 0;
        for (;;) {
          const {
            result: ftResult,
            format: fc,
            count: fcnt,
            formatText: ft,
            nextIndex,
          } = nextFormatItem(formatString, startIndex);
          startIndex = nextIndex;

          if (!ftResult) {
            break;
          }

          switch (fc) {
            case 'y':
            case 'Y':
              if (date.ty >= 0) {
                switch (fcnt) {
                  case 1:
                  case 4:
                    result += intToFixString(date.ty, 4);
                    break;
                  case 2:
                    if (displayText) {
                      result += intToFixString(date.ty % 100, 2);
                    } else {
                      result += intToFixString(date.ty, 4);
                    }
                    break;
                }
              }
              break;
            case 'm':
            case 'M':
              if (date.tm >= 0) {
                switch (fcnt) {
                  case 1:
                    result += intToStr(date.tm);
                    break;
                  case 2:
                    result += intToFixString(date.tm, 2);
                    break;
                  case 3:
                  case 4:
                    if (date.tm >= 1 && date.tm <= 12) {
                      result += fixCase(
                        getMonthText(date.tm, fcnt === 3),
                        fc === 'M'
                      );
                    }
                    break;
                }
              }
              break;
            case 'd':
            case 'D':
              if (date.td >= 0) {
                switch (fcnt) {
                  case 1:
                    result += intToStr(date.td);
                    break;
                  case 2:
                    result += intToFixString(date.td, 2);
                    break;
                  case 3:
                  case 4:
                    tmpDate = encodeDate(date);
                    if (tmpDate) {
                      result += fixCase(
                        getWeekDayText(tmpDate, fcnt === 3),
                        fc === 'D'
                      );
                    }
                    break;
                }
              }
              break;
            case 'e':
            case 'E':
              tmpDate = encodeDate(date);
              if (tmpDate) {
                if (fcnt === 1 || fcnt === 2) {
                  result += intToStr(getCasualWeekDayNumber(tmpDate));
                } else if (fcnt === 3 || fcnt === 4) {
                  result += fixCase(
                    getWeekDayText(tmpDate, fcnt === 3),
                    fc === 'E'
                  );
                }
              }
              break;
            case 'w':
            case 'W':
              tmpDate = encodeDate(date);
              if (tmpDate) {
                const { week } = getWeekNr(tmpDate);
                if (fcnt === 1) {
                  result += intToStr(week);
                } else if (fcnt === 2) {
                  result += intToFixString(week, 2);
                }
              }
              break;
            case 'o':
            case 'O':
              tmpDate = encodeDate(date);
              if (tmpDate) {
                const { year } = getWeekNr(tmpDate);
                if (fcnt === 1 || fcnt === 4) {
                  result += intToFixString(year, 4);
                } else if (fcnt === 2) {
                  result += displayText
                    ? intToFixString(year % 100, 2)
                    : intToFixString(year, 4);
                }
              }
              break;
            case 'x':
            case 'X':
              tmpDate = encodeDate(date);
              if (tmpDate) {
                if (fcnt === 1) {
                  result += intToStr(tmpDate.getUTCDate());
                } else if (fcnt === 2 || fcnt === 3) {
                  result += intToFixString(tmpDate.getUTCDate(), 3);
                }
              }
              break;
            case 'q':
            case 'Q':
              if (date.tm >= 1 && date.tm <= 12) {
                switch (fcnt) {
                  case 1:
                  case 2:
                    result += intToStr(getQuarter(date.tm));
                    break;
                  case 3:
                  case 4:
                    result += fixCase(getQuarterText(date.tm), false);
                    break;
                }
              }
              break;
            case 'h':
            case 'H':
              if (date.th >= 0) {
                switch (fcnt) {
                  case 1:
                    result += intToStr(date.th);
                    break;
                  case 2:
                    result += intToFixString(date.th, 2);
                    break;
                }
              }
              break;
            case 't':
            case 'T':
              if (date.th >= 0) {
                switch (fcnt) {
                  case 1:
                    result += intToStr(((date.th + 11) % 12) + 1);
                    break;
                  case 2:
                    result += intToFixString(((date.th + 11) % 12) + 1, 2);
                    break;
                }
              }
              break;
            case 'n':
            case 'N':
              if (date.tn >= 0) {
                switch (fcnt) {
                  case 1:
                    result += intToStr(date.tn);
                    break;
                  case 2:
                    result += intToFixString(date.tn, 2);
                }
              }
              break;
            case 's':
            case 'S':
              if (date.ts >= 0) {
                switch (fcnt) {
                  case 1:
                    result += intToStr(date.ts);
                    break;
                  case 2:
                    result += intToFixString(date.ts, 2);
                    break;
                }
              }
              break;
            case 'z':
            case 'Z':
              if (date.tms >= 0) {
                switch (fcnt) {
                  case 1:
                    result += intToStr(date.tms);
                    break;
                  case 2:
                    result += intToFixString(date.tms, 3);
                    break;
                }
              }
              break;
            case 'a':
            case 'A':
              if (date.th >= 0) {
                switch (fcnt) {
                  case 1: {
                    const amStr = formatterStrParamDef(
                      'AmStr',
                      params,
                      this.defaultParams()
                    );
                    const pmStr = formatterStrParamDef(
                      'PmStr',
                      params,
                      this.defaultParams()
                    );
                    result += date.th >= 1 && date.th <= 12 ? amStr : pmStr;
                    break;
                  }
                }
              }
              break;
            case '-':
              if (dsepcnt > 0) {
                result += formatterStrParamDef(
                  'DateSep',
                  params,
                  this.defaultParams()
                );
                dsepcnt--;
              }
              break;
            case ':':
              if (tsepcnt > 0) {
                result += formatterStrParamDef(
                  'TimeSep',
                  params,
                  this.defaultParams()
                );
                tsepcnt--;
              }
              break;
            case '.': {
              const { ty, tm, td, th, tn, ts, tms } = date;
              if (
                (ty >= 0 || tm >= 0 || td >= 0) &&
                (th >= 0 || tn >= 0 || ts >= 0 || tms >= 0)
              ) {
                result += formatterStrParamDef(
                  'DateTimeSep',
                  params,
                  this.defaultParams()
                );
              }
              break;
            }
            case "'":
            case '"':
              result += ft;
              break;
            case '(':
            case ')':
            case '|':
            case '/':
              break;
            default:
              result += fc;
              break;
          }
        }
      }
    }

    return result;
  }

  protected asDateTime(
    value: ValueType,
    params: string,
    defaultParams: string
  ): IInternalDateFormat {
    // om det inte finns något datum, anta 0 (30/12 - 1899)
    const date = extractDate(value, params, defaultParams);
    return {
      ty: date.getUTCFullYear(),
      tm: date.getUTCMonth() + 1, // dates in javascript start at 0
      td: date.getUTCDate(),
      th: date.getUTCHours(),
      tn: date.getUTCMinutes(),
      ts: date.getUTCSeconds(),
      tms: date.getUTCMilliseconds(),
    };
  }
}

// helpers
const yearPattern = (hideCentury: boolean) => (hideCentury ? 'yy' : 'yyyy');
const weekYearPattern = (hideCentury: boolean) => (hideCentury ? 'oo' : 'oooo');

const fixCase = (s: string, firstUpper: boolean): string => {
  if (firstUpper && s !== '') {
    return s.slice(0, 1).toLocaleUpperCase() + s.slice(1);
  }

  return s;
};

enum DayOfWeek {
  mon,
  tue,
  wed,
  thu,
  fri,
  sat,
  sun,
}

const dayList = [
  DayOfWeek.mon,
  DayOfWeek.tue,
  DayOfWeek.wed,
  DayOfWeek.thu,
  DayOfWeek.fri,
  DayOfWeek.sat,
  DayOfWeek.sun,
];

const getWeekDayText = (date: Date, shortText: boolean) => {
  return getWeekDayName(getWeekDay(date), shortText);
};

const getWeekDay = (date: Date): DayOfWeek => {
  return dayList[getWeekDayNumber(date)];
};

const weekDayNames: Record<DayOfWeek, [string, string]> = {
  [DayOfWeek.mon]: ['måndag', 'må'],
  [DayOfWeek.tue]: ['tisdag', 'ti'],
  [DayOfWeek.wed]: ['onsdag', 'on'],
  [DayOfWeek.thu]: ['torsdag', 'to'],
  [DayOfWeek.fri]: ['fredag', 'fr'],
  [DayOfWeek.sat]: ['lördag', 'lö'],
  [DayOfWeek.sun]: ['söndag', 'sö'],
};

/**
 * Returns the number of the day of 'date', with monday = 0
 */
const getWeekDayNumber = (date: Date): number => {
  let day = date.getUTCDay();
  if (day === 0) {
    // sunday
    day = 6;
  } else {
    day--;
  }

  return day;
};

/**
 * Returns the number of the day of 'date', with monday = 1
 */
const getCasualWeekDayNumber = (date: Date): number => {
  return getWeekDayNumber(date) + 1;
};

const getWeekDayName = (day: DayOfWeek, shortText: boolean) => {
  return weekDayNames[day][shortText ? 1 : 0];
};

const intToStr = (i: number) => {
  return String(Math.abs(i));
};

export { TEDCustomDateTimeDisplayer };
